
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  // Sidebar durumunu localStorage'da sakla
  useEffect(() => {
    const savedState = localStorage.getItem('sidebarCollapsed');
    if (savedState !== null) {
      setIsSidebarCollapsed(JSON.parse(savedState));
    }
  }, []);

  const toggleSidebar = () => {
    const newState = !isSidebarCollapsed;
    setIsSidebarCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', JSON.stringify(newState));
    
    // Body class'ını güncelle
    if (newState) {
      document.body.classList.add('sidebar-collapsed');
    } else {
      document.body.classList.remove('sidebar-collapsed');
    }
  };

  // Sayfa yüklendiğinde body class'ını ayarla
  useEffect(() => {
    if (isSidebarCollapsed) {
      document.body.classList.add('sidebar-collapsed');
    } else {
      document.body.classList.remove('sidebar-collapsed');
    }
  }, [isSidebarCollapsed]);

  const navigation = [
    { name: t('nav.home'), href: '/', icon: 'ri-home-line' },
    { name: t('nav.about'), href: '/hakkimizda', icon: 'ri-information-line' },
    { name: 'Ürünlerimiz', href: '/urunlerimiz', icon: 'ri-product-hunt-line' },
    { name: t('nav.contact'), href: '/iletisim', icon: 'ri-phone-line' },
  ];

  const socialMedia = [
    { 
      name: 'Facebook', 
      icon: 'ri-facebook-fill', 
      href: '#', 
      color: 'hover:text-blue-600' 
    },
    { 
      name: 'Instagram', 
      icon: 'ri-instagram-fill', 
      href: 'https://www.instagram.com/guventarsulama/', 
      color: 'hover:text-pink-600' 
    },
    { 
      name: 'LinkedIn', 
      icon: 'ri-linkedin-fill', 
      href: '#', 
      color: 'hover:text-blue-700' 
    },
    { 
      name: 'Twitter', 
      icon: 'ri-twitter-fill', 
      href: '#', 
      color: 'hover:text-blue-400' 
    }
  ];

  const toggleLanguage = () => {
    const newLang = i18n.language === 'tr' ? 'en' : 'tr';
    i18n.changeLanguage(newLang);
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className={`fixed left-0 top-0 h-full bg-white/95 backdrop-blur-xl border-r border-gray-200/50 z-50 hidden lg:block shadow-2xl shadow-gray-900/10 transition-all duration-300 ${
        isSidebarCollapsed ? 'w-20' : 'w-80'
      }`}>
        <div className="p-8 relative">
          {/* Gradient overlay for depth */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/50 via-transparent to-gray-50/30 pointer-events-none"></div>
          
          {/* Collapse Toggle Button */}
          <button
            onClick={toggleSidebar}
            className="absolute top-4 right-4 w-8 h-8 bg-white/80 hover:bg-white text-gray-600 hover:text-green-800 rounded-lg flex items-center justify-center shadow-md hover:shadow-lg transition-all duration-300 z-10 cursor-pointer"
            title={isSidebarCollapsed ? 'Menüyü Genişlet' : 'Menüyü Daralt'}
          >
            <i className={`ri-${isSidebarCollapsed ? 'menu-unfold' : 'menu-fold'}-line text-lg`}></i>
          </button>
          
          {/* Logo - Kapalı durumda da büyük kalacak */}
          <Link to="/" className={`relative flex items-center justify-center transition-all duration-300 ${isSidebarCollapsed ? 'mb-8 mt-12' : 'mb-8'}`}>
            {isSidebarCollapsed ? (
              <img 
                src="https://static.readdy.ai/image/6464ee4f71a2ee332e3d69d3173c1c98/a5a62d614849c9044927e6b1c729a1bb.png" 
                alt="GüvenTar Sulama Sistemleri Sembol" 
                className="w-20 h-20 object-contain hover:scale-105 transition-transform duration-300 drop-shadow-lg"
              />
            ) : (
              <img 
                src="https://static.readdy.ai/image/6464ee4f71a2ee332e3d69d3173c1c98/1821839d44cea169d9473da199a17a94.png" 
                alt="GüvenTar Sulama Sistemleri Logo" 
                className="w-36 h-36 object-contain hover:scale-105 transition-transform duration-300 drop-shadow-lg"
              />
            )}
          </Link>

          {/* Navigation */}
          <nav className={`space-y-4 mb-8 relative ${isSidebarCollapsed ? 'mb-4' : ''}`}>
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center rounded-xl transition-all duration-300 cursor-pointer shadow-sm hover:shadow-md group ${
                  isSidebarCollapsed ? 'px-3 py-3 justify-center' : 'px-6 py-4 space-x-4'
                } ${
                  location.pathname === item.href
                    ? 'bg-gradient-to-r from-green-800 to-green-700 text-white shadow-lg shadow-green-800/25'
                    : 'text-gray-700 hover:bg-white/80 hover:text-green-800 hover:shadow-lg hover:shadow-gray-200/50 bg-white/40'
                }`}
                title={isSidebarCollapsed ? item.name : ''}
              >
                <i className={`${item.icon} text-xl`}></i>
                {!isSidebarCollapsed && (
                  <span className="font-medium whitespace-nowrap">{item.name}</span>
                )}
              </Link>
            ))}
          </nav>

          {!isSidebarCollapsed && (
            <>
              {/* Social Media */}
              <div className="mb-8 relative">
                <h3 className="font-semibold text-gray-800 mb-4 text-sm">{t('common.followUs')}</h3>
                <div className="flex space-x-3">
                  {socialMedia.map((social) => (
                    <a
                      key={social.name}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`w-8 h-8 flex items-center justify-center rounded-lg bg-white/60 text-gray-600 transition-all duration-300 cursor-pointer ${social.color} hover:bg-white/90 shadow-sm hover:shadow-md hover:shadow-gray-200/50`}
                      title={social.name}
                    >
                      <i className={`${social.icon} text-sm`}></i>
                    </a>
                  ))}
                </div>
              </div>

              {/* Contact Info */}
              <div className="p-6 bg-white/70 backdrop-blur-sm rounded-xl border border-white/40 shadow-lg shadow-gray-200/50 relative">
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-xl pointer-events-none"></div>
                <h3 className="font-semibold text-gray-800 mb-3 relative">{t('nav.contact')}</h3>
                <div className="space-y-2 text-sm text-gray-600 relative">
                  <div className="flex items-center space-x-2">
                    <i className="ri-phone-line text-green-800"></i>
                    <span>+90 553 414 52 63</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <i className="ri-mail-line text-green-800"></i>
                    <span>info@guventar.com</span>
                  </div>
                  <div className="flex items-start space-x-2 mt-3">
                    <i className="ri-map-pin-line text-green-800 mt-0.5"></i>
                    <span className="text-xs leading-relaxed">
                      Cumalı, Antalya Cd. No:9 Serik/Antalya
                    </span>
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-white/30 relative">
                  <h4 className="font-semibold text-gray-800 mb-2 text-sm">{t('common.workingHours')}</h4>
                  <div className="text-xs text-gray-600 space-y-1">
                    <div>Pazartesi - Cumartesi</div>
                    <div className="font-medium">08:00 - 18:00</div>
                    <div className="text-red-600">Pazar: Kapalı</div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Right shadow gradient */}
          <div className="absolute top-0 right-0 w-8 h-full bg-gradient-to-l from-gray-900/5 to-transparent pointer-events-none"></div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 bg-white/10 backdrop-blur-md border-b border-white/20 z-50">
        <div className="flex justify-between items-center p-4">
          <div className="flex items-center space-x-3">
            {/* Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-lg bg-white/20 text-gray-700 hover:text-green-800 cursor-pointer"
            >
              <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
            </button>
            
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/6464ee4f71a2ee332e3d69d3173c1c98/1821839d44cea169d9473da199a17a94.png" 
                alt="GüvenTar Sulama Sistemleri Logo" 
                className="w-16 h-16 object-contain hover:scale-105 transition-transform duration-300"
              />
            </Link>
          </div>

          {/* Language Toggle Button */}
          <button
            onClick={toggleLanguage}
            className="flex items-center space-x-2 bg-white/20 backdrop-blur-sm text-gray-700 px-3 py-2 rounded-full border border-white/30 hover:bg-white/30 transition-all duration-300 cursor-pointer"
          >
            <div className="w-6 h-6 bg-gradient-to-br from-green-800 to-green-900 rounded-full flex items-center justify-center">
              <i className="ri-global-line text-white text-sm"></i>
            </div>
            <span className="text-sm font-semibold text-gray-800">
              {i18n.language === 'tr' ? 'EN' : 'TR'}
            </span>
          </button>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {isMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside className={`lg:hidden fixed left-0 top-0 h-full w-80 bg-white backdrop-blur-md border-r border-gray-200 z-50 transform transition-transform duration-300 ${
        isMenuOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="p-6">
          {/* Logo */}
          <Link to="/" className="flex items-center justify-center mb-8" onClick={() => setIsMenuOpen(false)}>
            <img 
              src="https://static.readdy.ai/image/6464ee4f71a2ee332e3d69d3173c1c98/1821839d44cea169d9473da199a17a94.png" 
              alt="GüvenTar Sulama Sistemleri Logo" 
              className="w-24 h-24 object-contain hover:scale-105 transition-transform duration-300"
            />
          </Link>

          {/* Navigation */}
          <nav className="space-y-3 mb-6">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  location.pathname === item.href
                    ? 'bg-green-800 text-white shadow-lg'
                    : 'text-gray-700 hover:bg-green-50 hover:text-green-800'
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                <i className={`${item.icon} text-lg`}></i>
                <span className="font-medium">{item.name}</span>
              </Link>
            ))}
          </nav>

          {/* Social Media */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-800 mb-3 text-sm">{t('common.followUs')}</h3>
            <div className="flex space-x-3">
              {socialMedia.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 text-gray-600 transition-all duration-300 cursor-pointer ${social.color} hover:bg-gray-200`}
                  title={social.name}
                >
                  <i className={`${social.icon} text-sm`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Contact Info */}
          <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
            <h3 className="font-semibold text-gray-800 mb-3 text-sm">{t('nav.contact')}</h3>
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex items-center space-x-2">
                <i className="ri-phone-line text-green-800"></i>
                <span>+90 553 414 52 63</span>
              </div>
              <div className="flex items-center space-x-2">
                <i className="ri-mail-line text-green-800"></i>
                <span>info@guventar.com</span>
              </div>
              <div className="flex items-start space-x-2 mt-3">
                <i className="ri-map-pin-line text-green-800 mt-0.5"></i>
                <span className="text-xs leading-relaxed">
                  Cumalı, Antalya Cd. No:9 Serik/Antalya
                </span>
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-gray-200">
              <h4 className="font-semibold text-gray-800 mb-2 text-sm">{t('common.workingHours')}</h4>
              <div className="text-xs text-gray-600 space-y-1">
                <div>Pazartesi - Cumartesi</div>
                <div className="font-medium">08:00 - 18:00</div>
                <div className="text-red-600">Pazar: Kapalı</div>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
